import React, {useState, useEffect} from 'react';
import './IngestionFilter.scss';
import {Box} from '@mui/material';
import {t} from 'i18next';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrMultiSelectDropdown from '../../common/arr-select-dropdown/ArrMultiSelectDropdown';
import {ISelectItem} from '../../../shared/types';
import ArrCheckBox from '../../common/arr-checkbox/ArrCheckbox';

interface IngestionFilterProps {
  options: ISelectItem[]; // for sortFields
  statusOptions: ISelectItem[]; // for ingestionStateList
  defaultSingleValue?: ISelectItem; // optional default for single select
  defaultMultiValues?: ISelectItem[]; // optional default for multi select
  onApplyFilters: (
    sortByingestion: ISelectItem | null,
    filterByingestion: ISelectItem[],
    showAll: boolean,
    ingestionNameSearchTerm: string
  ) => void;
}

const IngestionFilter: React.FC<IngestionFilterProps> = ({
  options,
  statusOptions,
  defaultSingleValue,
  defaultMultiValues,
  onApplyFilters,
}) => {
  const [singleValue, setSingleValue] = useState<ISelectItem | null>(null);
  const [selectedMulti, setSelectedMulti] = useState<ISelectItem[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  // useEffect to initialize default values
  useEffect(() => {
    if (defaultSingleValue) {
      setSingleValue({
        id: defaultSingleValue.value,
        name: defaultSingleValue.label,
      });
    } else {
      setSingleValue(null);
    }
    if (defaultMultiValues && defaultMultiValues.length > 0) {
      const transformed = defaultMultiValues.map((item) => ({
        id: item.value,
        name: item.label,
      }));
      setSelectedMulti(transformed);
    } else {
      setSelectedMulti([]);
    }
  }, [defaultSingleValue, defaultMultiValues]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      onApplyFilters(singleValue, selectedMulti, showAll, searchInputValue);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInputValue]);
  // transform dropdown options
  const optionsToPass: ISelectItem[] = options
    .filter((opt) => opt.value !== 'ProjectName')
    .map((opt) => ({
      id: opt.value,
      name: opt.label,
    }));

  const optionsToPassToMulti: ISelectItem[] = statusOptions.map((opt) => ({
    id: opt.value,
    name: opt.label,
  }));

  const handleSingleChange = (value: ISelectItem | null) => {
    setSingleValue(value);
    onApplyFilters(value, selectedMulti, showAll, searchInputValue);
  };

  const handleMultiChange = (values: ISelectItem[]) => {
    setSelectedMulti(values);
    onApplyFilters(singleValue, values, showAll, searchInputValue);
  };

  const handleShowAllChange = (value: boolean) => {
    setShowAll(value);
    onApplyFilters(singleValue, selectedMulti, value, searchInputValue);
  };

  return (
    <Box className="ingestion-header">
      <Box className="ingestion-search">
        <ArrTextField
          sizeVariant="large"
          className="search-input"
          placeholder={t('ingestions.mapped.ingestionsName')}
          type="search"
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.target.value)}
          dataTestId="ingestions-search-text"
        />
      </Box>

      <Box className="ingestion-filters">
        <ArrSingleSelectDropdown
          dropdownLabel={t('common.sortby')}
          sizeVariant="large"
          dropdownItems={optionsToPass}
          selectedValues={singleValue} // use state, not prop
          onSelectionChange={handleSingleChange} // Now accessible!
          customWidth={18}
          dataTestId="asd-ingestion-filters-singleValue"
        />

        <ArrMultiSelectDropdown
          dropdownLabel={t('common.filterby')}
          dropdownItems={optionsToPassToMulti}
          selectedValues={selectedMulti} //  use state, not prop
          onSelectionChange={handleMultiChange}
          multiple
          sizeVariant="large"
          searchable
          withSelectAll={false}
          customWidth={18}
          checkboxDataTestId="ingestion-filter-dropdown-checkbox"
        />
      </Box>

      <Box className="checkbox-section">
        <ArrCheckBox
          name=""
          labelText={t('ingestions.showAll')}
          size="small"
          checked={showAll}
          onChange={handleShowAllChange}
          labelPlacement="start"
          aria-label={`show-all-cb`}
        />
      </Box>
    </Box>
  );
};

export default IngestionFilter;
